import { withSentryReactRouterV6Routing } from '@sentry/react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Sentry } from "@/sentry";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import Layout from "@/components/Layout";
import HomePage from "@/components/HomePage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import LateralDashboard from "@/pages/admin/lateral/LateralDashboard";
import LateralJobPositionsPage from "@/pages/admin/lateral/LateralJobPositionsPage";
import LateralCandidatesPage from "@/pages/admin/lateral/LateralCandidatesPage";
import CreateLateralCandidatePage from "@/pages/admin/lateral/CreateLateralCandidatePage";
import BulkCVEvaluationPage from "@/pages/admin/lateral/BulkCVEvaluationPage";
import LateralKanbanPage from "@/pages/admin/lateral/LateralKanbanPage";
import EditLateralCandidatePage from "@/pages/admin/lateral/EditLateralCandidatePage";
import LateralCandidateEvaluationPage from "@/pages/admin/lateral/LateralCandidateEvaluationPage";
import CandidatesPage from "@/pages/admin/CandidatesPage";
import CreateCandidatePage from "@/pages/admin/CreateCandidatePage";
import EditCandidatePage from "@/pages/admin/EditCandidatePage";
import CollegesPage from "@/pages/admin/CollegesPage";
import QuestionsPage from "@/pages/admin/QuestionsPage";
import JobPositionsPage from "@/pages/admin/JobPositionsPage";
import AIQuestionTemplatesPage from "@/pages/admin/AIQuestionTemplatesPage";
import PromptsPage from "@/pages/admin/PromptsPage";
import CandidateEvaluationPage from "@/pages/admin/CandidateEvaluationPage";
import VoiceInterviewPage from "@/pages/admin/VoiceInterviewPage";
import ChangePasswordPage from "@/pages/admin/ChangePasswordPage";
import SimulateAssessmentPage from "@/pages/admin/SimulateAssessmentPage";
import SentryTestPage from "@/pages/admin/SentryTestPage";
import CandidateJourneyGuidePage from "@/pages/admin/CandidateJourneyGuidePage";
import PublicCandidateRegistration from "@/pages/PublicCandidateRegistration";
import PublicAssessment from "@/pages/PublicAssessment";
import NotFound from "@/pages/NotFound";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => Sentry.captureException(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => Sentry.captureException(error),
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading authentication...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

function LateralRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading, profileLoading } = useAuth();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/admin/login" replace />;
  }

  if (!profile?.has_lateral_access && profile?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppContent() {
  return (
    <Router>
      <AuthProvider>
        <SentryRoutes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route
            path="/admin/login"
            element={
              <PublicRoute>
                <AuthPage />
              </PublicRoute>
            }
          />
          <Route path="/auth" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          {/* Routes that need ServerTimeProvider for time synchronization */}
          <Route
            path="/register/:collegeCode"
            element={<PublicCandidateRegistration />}
          />
          <Route path="/assessment" element={<PublicAssessment />} />

          {/* Private routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidates"
            element={
              <PrivateRoute>
                <Layout>
                  <CandidatesPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidates/create"
            element={
              <PrivateRoute>
                <Layout>
                  <CreateCandidatePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidates/:candidateId/edit"
            element={
              <PrivateRoute>
                <Layout>
                  <EditCandidatePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidates/:candidateId/evaluate"
            element={
              <PrivateRoute>
                <Layout>
                  <CandidateEvaluationPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidates/:candidateId/voice-interview"
            element={
              <PrivateRoute>
                <Layout>
                  <VoiceInterviewPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/colleges"
            element={
              <PrivateRoute>
                <Layout>
                  <CollegesPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/questions"
            element={
              <PrivateRoute>
                <Layout>
                  <QuestionsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/job-positions"
            element={
              <PrivateRoute>
                <Layout>
                  <JobPositionsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/job-positions/:jobPositionId/simulate"
            element={
              <PrivateRoute>
                <Layout>
                  <SimulateAssessmentPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/ai-templates"
            element={
              <PrivateRoute>
                <Layout>
                  <AIQuestionTemplatesPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/prompts"
            element={
              <PrivateRoute>
                <Layout>
                  <PromptsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/candidate-journey"
            element={
              <PrivateRoute>
                <Layout>
                  <CandidateJourneyGuidePage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/change-password"
            element={
              <PrivateRoute>
                <Layout>
                  <ChangePasswordPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sentry-test"
            element={
              <PrivateRoute>
                <Layout>
                  <SentryTestPage />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Lateral panel routes */}
          <Route
            path="/admin/lateral"
            element={
              <LateralRoute>
                <Layout>
                  <LateralDashboard />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/job-positions"
            element={
              <LateralRoute>
                <Layout>
                  <LateralJobPositionsPage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/candidates"
            element={
              <LateralRoute>
                <Layout>
                  <LateralCandidatesPage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/candidates/create"
            element={
              <LateralRoute>
                <Layout>
                  <CreateLateralCandidatePage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/cv-evaluation"
            element={
              <LateralRoute>
                <Layout>
                  <BulkCVEvaluationPage />
                </Layout>
              </LateralRoute>
            }
          />

          <Route
            path="/admin/lateral/candidates/:candidateId/edit"
            element={
              <LateralRoute>
                <Layout>
                  <EditLateralCandidatePage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/candidates/:candidateId/evaluate"
            element={
              <LateralRoute>
                <Layout>
                  <LateralCandidateEvaluationPage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/kanban/:positionId"
            element={
              <LateralRoute>
                <Layout>
                  <LateralKanbanPage />
                </Layout>
              </LateralRoute>
            }
          />
          <Route
            path="/admin/lateral/kanban"
            element={
              <LateralRoute>
                <Layout>
                  <LateralKanbanPage />
                </Layout>
              </LateralRoute>
            }
          />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
        <Toaster />
      </AuthProvider>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
